// 커리어관리 입력 이벤트 유틸
import { ChangeEvent } from 'react'

type TChangeOnlyNumberAndDotFn = (e: ChangeEvent<HTMLInputElement>) => void
type TChangeDateToDotFn = (e: ChangeEvent<HTMLInputElement>) => void
type TChangeOnlyNumberFn = (e: ChangeEvent<HTMLInputElement>) => void
type TChangePhoneNumberFn = (e: ChangeEvent<HTMLInputElement>) => void
type TFromatDateYearMonthFn = (num?: number) => string | undefined
type TChangeInputMaxLengthFn = (
  e: ChangeEvent<HTMLTextAreaElement>,
  maxLength: number,
) => void
type TIsArrayFullyPopulatedFn = <T>(array: Array<T>) => boolean

/**
 * @desc 년월 입력 필드에서 숫자와 마침표만 입력이 가능하도록 처라하고 형식에 맞도록 반환합니다
 */
export const changeOnlyNumberAndDot: TChangeOnlyNumberAndDotFn = (e) => {
  const inputText = e.target.value

  e.target.value = inputText.replace(/[^0-9.]/g, '')
}

/**
 * @desc 년월일 입력 필드에서 숫자 입력시 마침표를 자동으로 할당합니다.
 */
export const changeDateToDot: TChangeDateToDotFn = (e) => {
  let inputText = e.target.value.replace(/[^0-9]/g, '')
  if (inputText.length > 4 && inputText.length <= 6) {
    inputText = inputText.replace(/([0-9]{4})([0-9]{0,2})/, '$1.$2')
  } else if (inputText.length > 6) {
    inputText = inputText.replace(
      /([0-9]{4})([0-9]{2})([0-9]{0,2})/,
      '$1.$2.$3',
    )
  }
  e.target.value = inputText
}

/**
 * @desc 0-9까지의 숫자만 입력받을 수 있도록 처리합니다
 */
export const changeOnlyNumber: TChangeOnlyNumberFn = (e) => {
  const inputText = e.target.value.toString()

  e.target.value = inputText.replace(/[^0-9]/g, '')
}

/**
 * @desc 휴대폰 번호 입력시 숫자만 입력받을 수 있도록 처리하고 형식에 맞도록 반환합니다
 */
export const changePhoneNumber: TChangePhoneNumberFn = (e) => {
  const onlyNumber = e.target.value.replace(/\D/g, '')

  switch (onlyNumber.length) {
    case 10: // 010-123-4567
      e.target.value = onlyNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      return
    case 11: // 010-1234-5678
      e.target.value = onlyNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      return
    default:
      e.target.value = onlyNumber
      return
  }
}

/**
 * @desc api를 통해 가져온 재직기간의 기간 정보를 YYYY.MM 형식의 문자열로 변환합니다
 */
export const formatDateYearMonth: TFromatDateYearMonthFn = (num) => {
  if (!num) return

  const strNum = num.toString()
  const year = strNum.substring(0, 4)
  const month = strNum.substring(4)
  return `${year}.${month}`
}

/**
 * @desc 입력 가능한 텍스트 수를 제한하고 입력 수 이상 입력시 아무것도 입력되지 않도록 합니다
 * @param e input event
 * @param maxLength 최대 입력 가능 텍스트 수
 * @returns
 */
export const changeInputMaxLength: TChangeInputMaxLengthFn = (e, maxLength) => {
  const inputText = e.target.value

  if (inputText.length >= maxLength) {
    e.target.value = e.target.value.substring(0, maxLength)
    return
  }
}

/**
 * @desc 배열 내부의 값을 탐색해 해당 값이 비어있는지 판별합니다.
 * @param array 체크할 데이터 배열
 * @returns {boolean} 하나라도 비어있지 않으면 true, 모두 비어 있으면 false
 */
export const isArrayFullyPopulated: TIsArrayFullyPopulatedFn = <T>(
  array: Array<T>,
): boolean => {
  const checkValue = (value: T): boolean => {
    if (Array.isArray(value)) {
      return value.some(checkValue)
    } else if (typeof value === 'object' && value !== null) {
      const values = Object.values(value)
      return values.length > 0 && values.some(checkValue)
    } else {
      return Boolean(value)
    }
  }
  return array.some(checkValue)
}
